import { Suspense, lazy, useEffect, useMemo, type ReactNode } from "react";
import { WORLDS, type WorldMeta } from "./registry";

function upsertMeta(attrName: string, attrValue: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attrName}="${attrValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Applies the world's document metadata and the `data-world` attribute.
 *
 * This lives INSIDE the Suspense boundary on purpose. Effects do not run until the
 * suspended subtree commits, so `html[data-world]` appears at the moment the world's
 * DOM is actually on screen — never while the fallback is still showing.
 *
 * That matters beyond tidiness: `data-world` is the signal every spec waits on to decide
 * the world is ready. When it was set from the host (outside Suspense) it went up one or
 * two chunk-fetches early, and specs that queried the DOM immediately afterwards raced
 * the world's first render and read null.
 */
function WorldFrame({ meta, children }: { meta: WorldMeta; children: ReactNode }) {
  useEffect(() => {
    const root = document.documentElement;
    const previousTitle = document.title;
    root.setAttribute("data-world", meta.slug);
    document.title = meta.title;
    upsertMeta("name", "description", meta.description);
    upsertMeta("property", "og:title", meta.title);
    upsertMeta("property", "og:description", meta.description);
    upsertMeta("property", "og:type", "website");
    return () => {
      root.removeAttribute("data-world");
      document.title = previousTitle;
    };
  }, [meta]);

  return <>{children}</>;
}

/**
 * Renders one Fable World full-page, isolated from the Foundry shell.
 *
 * The shell's stylesheets and bundle are never loaded on a world route (see src/main.tsx),
 * so a world ships only its own bytes. This module is imported statically by main.tsx —
 * it is under a kilobyte, and behind its own dynamic import it added a second serial
 * round-trip in front of every world.
 */
export default function WorldHost({ slug }: { slug: string }) {
  const entry = WORLDS[slug];

  const Page = useMemo(
    () => lazy(async () => ({ default: (await entry.load()).default })),
    [entry]
  );

  return (
    <Suspense fallback={<div aria-busy="true" style={{ minHeight: "100vh" }} />}>
      <WorldFrame meta={entry.meta}>
        <Page />
      </WorldFrame>
    </Suspense>
  );
}
